import React, { useMemo, useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useAdminData } from '../contexts/AdminDataContext'
import { supabaseAdmin } from '../lib/supabaseClient'
import { Search, CheckSquare, Send, Loader2 } from 'lucide-react'
import { useToast, ToastContainer } from '../hooks/useToast'

const Messages: React.FC = () => {
  const { usuarios, loading, loadUsuarios } = useAdminData()
  const { toasts, removeToast, success, error, warning } = useToast()

  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<'all'|'active'|'inactive'>('all')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [selected, setSelected] = useState<Record<string, boolean>>({})
  const [text, setText] = useState('Olá {nome}!')
  const [sending, setSending] = useState(false)
  const [result, setResult] = useState<any|null>(null)

  // Carregar usuários ao montar o componente
  useEffect(() => {
    console.log('🔍 Messages component mounted, usuarios:', usuarios.length)
    if (usuarios.length === 0) {
      console.log('📞 Loading usuarios...')
      loadUsuarios()
    }
  }, [usuarios.length, loadUsuarios])

  const filtered = useMemo(() => {
    const term = search.toLowerCase()
    return usuarios.filter(u => {
      const matchTerm = !term || (
        (u.nome||'').toLowerCase().includes(term) ||
        (u.email||'').toLowerCase().includes(term) ||
        (u.whatsapp||'').toLowerCase().includes(term)
      )
      const matchStatus = status === 'all' || (status === 'active' ? u.ativo : !u.ativo)
      const d = new Date(u.created_at)
      const matchDate = (!from || d >= new Date(from)) && (!to || d <= new Date(to + 'T23:59:59'))
      return matchTerm && matchStatus && matchDate
    })
  }, [usuarios, search, status, from, to])

  const toggleSelectAll = () => {
    const allSel = filtered.every(u => selected[u.id])
    if (allSel) {
      const copy = { ...selected }
      filtered.forEach(u => delete copy[u.id])
      setSelected(copy)
    } else {
      const copy = { ...selected }
      filtered.forEach(u => { copy[u.id] = true })
      setSelected(copy)
    }
  }

  const handleSend = async () => {
    const ids = Object.keys(selected).filter(id => selected[id])
    if (ids.length === 0) { 
      warning('Atenção', 'Selecione pelo menos um usuário para enviar a mensagem')
      return 
    }
    if (!text.trim()) { 
      warning('Atenção', 'Digite uma mensagem antes de enviar')
      return 
    }

    setSending(true)
    setResult(null)
    
    try {
      // Configurações UAZAPI - você pode mover para variáveis de ambiente
      const UAZAPI_URL = 'https://aguiarsystem.uazapi.com/send/text'
      const UAZAPI_APIKEY = '7967bf10-352a-4a50-baf8-621a7948e498'
      
      // Monta usuários selecionados
      const usersMap = new Map(usuarios.map(u => [u.id, u]))
      const selectedUsers = ids
        .map(id => usersMap.get(id))
        .filter(Boolean)

      console.log('🚀 Enviando mensagens diretamente via UAZAPI')
      console.log('👥 Usuários selecionados:', selectedUsers.length)
      
      const results = []
      
      // Enviar mensagem para cada usuário individualmente
      for (const user of selectedUsers) {
        if (!user) continue
        
        const number = normalizePhoneBR(user.whatsapp || '')
        const personalizedMessage = text.replace(/{nome}/g, user.nome || 'Cliente')
        
        console.log(`📱 Enviando para ${user.nome} (${number}): ${personalizedMessage}`)
        
        try {
          const resp = await fetch(UAZAPI_URL, {
            method: 'POST',
            headers: { 
              'Accept': 'application/json',
              'Content-Type': 'application/json',
              'token': UAZAPI_APIKEY
            },
            body: JSON.stringify({
              number,
              text: personalizedMessage
            })
          })
          
          const success = resp.ok
          console.log(`${success ? '✅' : '❌'} ${user.nome}: ${resp.status}`)
          
          results.push({
            user_id: user.id,
            nome: user.nome,
            whatsapp: number,
            message_sent: personalizedMessage,
            ok: success,
            status: resp.status
          })
          
          // Delay entre envios para não sobrecarregar
          if (selectedUsers.length > 1) {
            await new Promise(resolve => setTimeout(resolve, 500))
          }
          
        } catch (error) {
          console.error(`❌ Erro ao enviar para ${user.nome}:`, error)
          results.push({
            user_id: user.id,
            nome: user.nome,
            whatsapp: number,
            message_sent: personalizedMessage,
            ok: false,
            error: String(error)
          })
        }
      }
      
      const successCount = results.filter(r => r.ok).length
      const errorCount = results.filter(r => !r.ok).length
      
      setResult({ 
        ok: true, 
        count: results.length, 
        success: successCount,
        errors: errorCount,
        results 
      })
      
      if (successCount > 0 && errorCount === 0) {
        success('Mensagens enviadas!', `${successCount} mensagem${successCount > 1 ? 's' : ''} enviada${successCount > 1 ? 's' : ''} com sucesso`)
      } else if (successCount > 0 && errorCount > 0) {
        warning('Envio parcial', `${successCount} sucesso, ${errorCount} erro${errorCount > 1 ? 's' : ''}`)
      } else {
        error('Falha no envio', `Nenhuma mensagem foi enviada com sucesso`)
      }
      
    } catch (e: any) {
      console.error('❌ Erro geral:', e)
      error('Erro no envio', e.message)
    } finally {
      setSending(false)
    }
  }

  // Função para normalizar número de telefone brasileiro
  const normalizePhoneBR = (raw: string) => {
    const digits = String(raw || '').replace(/\D/g, '')
    if (!digits) return ''
    return digits.startsWith('55') ? digits : `55${digits}`
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Envio de Mensagens</h1>
          <p className="text-gray-600 mt-2">Filtre usuários, selecione e envie mensagens. Use {'{nome}'} no texto.</p>
        </div>
        <button onClick={() => loadUsuarios(true)} className="admin-button admin-button-secondary">Atualizar lista</button>
      </div>

      <div className="admin-card space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input className="admin-input pl-10" placeholder="Nome, email ou WhatsApp" value={search} onChange={(e)=>setSearch(e.target.value)} />
          </div>
          <select className="admin-input" value={status} onChange={(e)=>setStatus(e.target.value as any)}>
            <option value="all">Todos</option>
            <option value="active">Ativos</option>
            <option value="inactive">Inativos</option>
          </select>
          <input type="date" className="admin-input" value={from} onChange={(e)=>setFrom(e.target.value)} />
          <input type="date" className="admin-input" value={to} onChange={(e)=>setTo(e.target.value)} />
        </div>
        <div className="flex items-center justify-between">
          <button onClick={toggleSelectAll} className="admin-button admin-button-secondary flex items-center"><CheckSquare className="w-4 h-4 mr-2"/>Selecionar/Desmarcar filtrados</button>
          <div className="text-sm text-gray-600">Selecionados: {Object.values(selected).filter(Boolean).length} / Filtrados: {filtered.length}</div>
        </div>
      </div>

      <div className="admin-card">
        <textarea rows={4} className="admin-input w-full" value={text} onChange={(e)=>setText(e.target.value)} placeholder="Digite a mensagem. Use {nome}." />
        <div className="flex justify-end mt-3">
          <button onClick={handleSend} disabled={sending} className="admin-button admin-button-primary flex items-center">
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Send className="w-4 h-4 mr-2"/>}
            Enviar Mensagem
          </button>
        </div>
      </div>

      <motion.div initial={{opacity:0}} animate={{opacity:1}} className="admin-card">
        {loading ? (
          <div className="text-gray-500">Carregando...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2">Sel.</th>
                  <th className="px-4 py-2 text-left">Usuário</th>
                  <th className="px-4 py-2 text-left">WhatsApp</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-left">Cadastro</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filtered.map(u => (
                  <tr key={u.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-center">
                      <input type="checkbox" checked={!!selected[u.id]} onChange={(e)=>setSelected(prev=>({ ...prev, [u.id]: e.target.checked }))} />
                    </td>
                    <td className="px-4 py-2">
                      <div className="font-medium text-gray-900">{u.nome || '(Sem nome)'}</div>
                      <div className="text-sm text-gray-500">{u.email}</div>
                    </td>
                    <td className="px-4 py-2 text-gray-700">{u.whatsapp || '-'}</td>
                    <td className="px-4 py-2">{u.ativo ? <span className="text-green-600">Ativo</span> : <span className="text-red-600">Inativo</span>}</td>
                    <td className="px-4 py-2 text-gray-500">{new Date(u.created_at).toLocaleDateString('pt-BR')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>

      {result && (
        <div className="admin-card">
          <h3 className="text-lg font-semibold mb-4">Resultados do Envio</h3>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="bg-blue-50 p-3 rounded">
              <div className="text-2xl font-bold text-blue-600">{result.count}</div>
              <div className="text-sm text-blue-600">Total</div>
            </div>
            <div className="bg-green-50 p-3 rounded">
              <div className="text-2xl font-bold text-green-600">{result.success}</div>
              <div className="text-sm text-green-600">Sucesso</div>
            </div>
            <div className="bg-red-50 p-3 rounded">
              <div className="text-2xl font-bold text-red-600">{result.errors}</div>
              <div className="text-sm text-red-600">Erros</div>
            </div>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-left">Usuário</th>
                  <th className="px-4 py-2 text-left">WhatsApp</th>
                  <th className="px-4 py-2 text-left">Mensagem</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {result.results?.map((r: any, idx: number) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-2">
                      {r.ok ? (
                        <span className="text-green-600 font-medium">✅ Enviado</span>
                      ) : (
                        <span className="text-red-600 font-medium">❌ Erro</span>
                      )}
                    </td>
                    <td className="px-4 py-2 font-medium">{r.nome}</td>
                    <td className="px-4 py-2">{r.whatsapp}</td>
                    <td className="px-4 py-2 text-sm max-w-xs truncate" title={r.message_sent}>
                      {r.message_sent}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Toast Container */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  )
}

export default Messages
